import { Macro } from '@chiroup/core/types/Macro';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type Props = {
  showPopover: boolean;
  setShowPopover: React.Dispatch<React.SetStateAction<boolean>>;
  filteredMacros: Partial<Macro>[];
  handleMacroClick: (phrase: string, cursorPosition?: number) => void;
  cursorPosition?: number;
  placement?: 'top' | 'bottom';
  anchorRef?: React.RefObject<HTMLElement>;
};

const MacrosPopover: React.FC<Props> = ({
  showPopover,
  filteredMacros,
  handleMacroClick,
  cursorPosition,
  setShowPopover,
  placement = 'bottom',
  anchorRef,
}) => {
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const popoverRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(
    null
  );

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const listItems = listRef.current.querySelectorAll('li');
      const highlightedItem = listItems[highlightedIndex];

      if (highlightedItem) {
        highlightedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex]);

  // Calculate position based on anchor element
  useLayoutEffect(() => {
    if (showPopover && anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const popoverHeight = popoverRef.current?.offsetHeight || 200;

      // Determine if we should show above or below
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldShowAbove =
        placement === 'top' || (spaceBelow < popoverHeight && spaceAbove > spaceBelow);

      setPosition({
        top: shouldShowAbove ? rect.top - popoverHeight - 8 : rect.bottom + 8,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [showPopover, anchorRef, placement]);

  // Update position on scroll/resize
  useEffect(() => {
    if (!showPopover || !anchorRef?.current) return;

    const updatePosition = () => {
      const rect = anchorRef.current!.getBoundingClientRect();
      const popoverHeight = popoverRef.current?.offsetHeight || 200;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldShowAbove =
        placement === 'top' || (spaceBelow < popoverHeight && spaceAbove > spaceBelow);

      setPosition({
        top: shouldShowAbove ? rect.top - popoverHeight - 8 : rect.bottom + 8,
        left: rect.left,
        width: rect.width,
      });
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showPopover, anchorRef, placement]);

  useEffect(() => {
    if (!filteredMacros.length) {
      setShowPopover(false);
    }
  }, [filteredMacros, setShowPopover]);

  // Click-outside handler to dismiss popover
  useEffect(() => {
    if (!showPopover) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Check if click is inside the popover using data attribute
      if (target.closest('[data-macros-popover]')) {
        return;
      }

      // Check if click is on the anchor element (input/textarea)
      if (anchorRef?.current && anchorRef.current.contains(target)) {
        return;
      }

      setShowPopover(false);
    };

    // Small delay to avoid closing on the same click that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPopover, setShowPopover, anchorRef]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showPopover) {
        if (e.key === 'Tab' || e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();

          const selectedIndex = highlightedIndex >= 0 ? highlightedIndex : 0;
          const selectedMacro = filteredMacros[selectedIndex];

          if (selectedMacro?.phrase) {
            handleMacroClick(selectedMacro.phrase, cursorPosition ?? 0);
          }
        } else if (e.key === 'ArrowDown') {
          const macroLength = filteredMacros.length;
          e.preventDefault();
          setHighlightedIndex(prevIndex => {
            const newIndex = prevIndex + 1;

            return newIndex + 1 > macroLength ? 0 : newIndex;
          });
        } else if (e.key === 'ArrowUp') {
          const macroLength = filteredMacros.length;
          e.preventDefault();
          setHighlightedIndex(prevIndex => {
            const newIndex = prevIndex - 1;

            return newIndex < 0 ? macroLength - 1 : newIndex;
          });
        } else if (e.key === 'Backspace') {
          setShowPopover(false);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          setShowPopover(false);
        }
      }
    };

    // Use capture phase so macro key handling runs before React synthetic onKeyDown.
    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [
    highlightedIndex,
    showPopover,
    cursorPosition,
    filteredMacros,
    handleMacroClick,
    setShowPopover,
  ]);

  // If we have an anchor ref, use portal to render outside modal
  const popoverContent = showPopover ? (
    <div
      ref={popoverRef}
      data-macros-popover
      className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg flex flex-col"
      style={
        anchorRef
          ? {
              position: 'fixed',
              zIndex: 99999,
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              width: position?.width ?? 'auto',
              maxWidth: '32rem',
              maxHeight: 'calc(100vh - 100px)',
              pointerEvents: 'auto',
            }
          : {
              position: 'absolute',
              zIndex: 99999,
              width: '100%',
              maxWidth: '32rem',
              maxHeight: 'calc(100vh - 100px)',
              pointerEvents: 'auto',
              ...(placement === 'top'
                ? { bottom: '100%', marginBottom: '0.5rem' }
                : { marginTop: '0.5rem' }),
            }
      }
    >
      <p className="italic p-1 text-xs text-gray-500 dark:text-gray-300 b-2 flex items-center bg-gray-300 dark:bg-gray-700 px-2 border border-gray-300 dark:border-gray-600 rounded-t-md">
        <span>
          <InformationCircleIcon className="h-4 w-4 mr-2" />
        </span>
        <span>Click on shortcut or press tab to use macro</span>
      </p>
      <div className="p-2 max-h-48 overflow-y-auto">
        <ul ref={listRef} className="list-none mt-2">
          {filteredMacros?.map((macro, index) => (
            <li
              key={index}
              className={`cursor-pointer py-1 px-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 ${
                index === highlightedIndex ? 'bg-gray-100 dark:bg-gray-700' : ''
              } rounded-md`}
              onClick={() => {
                handleMacroClick(macro.phrase ?? '', cursorPosition);
              }}
            >
              {macro.shortcut}
            </li>
          ))}
        </ul>
      </div>
    </div>
  ) : null;

  // Use portal if we have an anchor ref (to escape modal overflow)
  if (anchorRef && showPopover && position) {
    return createPortal(popoverContent, document.body);
  }

  // Fallback to relative positioning if no anchor ref
  return <div className="relative w-full">{popoverContent}</div>;
};

export default MacrosPopover;
